/**
 * An organization's audit (§5.25): who did what to its projects, its settings,
 * its secrets and the machines that read them — for the owner and the
 * administrators, and never pruned.
 *
 * A line carries paths and versions, never a value, and names the machine
 * whenever an app did the thing: a secret read is the one *read* recorded here,
 * because it is the one that hands over something that cannot be taken back.
 * A line that produced a version opens that version's history.
 *
 * The filters answer a **question**, which is why three of them hold sets and
 * the dates are a period. "What did these two people do to those three
 * workspaces last week" is one question; with a control that holds one value it
 * was six readings to run and add up by hand. Empty is no filter rather than no
 * result, so clearing the last tick is the page as it opened.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { AUDIT_ACTIONS } from '@airlock/contracts/cloud/wire';
import { color, font } from '../../theme/tokens';
import { Empty, Input, Row, RowAction, Table } from '../../components/primitives';
import { HeaderButton } from '../../components/Shell';
import { MultiSelect } from '../../components/MultiSelect';
import { api, API_BASE, apiPath, apiQuery } from '../../lib/api';
import type { AuditEntry, AuditPage as AuditAnswer, Me, WorkspaceList, WorkspaceSummary, Seat } from '../../lib/types';
import { OrgPicker, actorLabel, adminOrgs, failureText } from './shared';
import { DateCell } from '../../components/DateCell';
import { notify } from '../../components/Toast';
import { PageError, SkeletonRows, useLoadPhase } from '../../components/Loading';
import { FilterBar, FilterCount, QuietButton, filterDate } from '../../components/FilterBar';
import { WithTooltip } from '../../components/Tooltip';

/** The lines that produced a version worth opening. */
const VERSIONED = new Set(['project.create', 'project.publish', 'project.restore']);

export function AuditPage({
  me,
  orgId,
  onOrg,
  onOpenRevision
}: {
  me: Me;
  orgId: string | null;
  onOrg: (orgId: string) => void;
  onOpenRevision: (orgId: string, projectId: string, version: number) => void;
}): React.JSX.Element {
  const orgs = adminOrgs(me);
  const chosen = orgs.find((organization) => organization.id === orgId) ?? orgs[0] ?? null;
  // Sets, not values: a trail is read by naming the two people and the three
  // workspaces the question is about, and an empty set is no filter at all.
  const [projects, setProjects] = useState<string[]>([]);
  const [actors, setActors] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  // A period, not a date. `since` alone answers "since when", which is the only
  // question one date can ask; a month, a sprint or an incident has two ends.
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [older, setOlder] = useState(false);
  const phase = useLoadPhase(entries === null);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [error, setError] = useState<string | null>(null);
  const columns = '130px minmax(0,1fr) 140px minmax(0,1fr) minmax(0,1.3fr) 110px';

  const query = useCallback(
    (after: string | null): string => {
      const params = new URLSearchParams();
      // Repeated rather than comma-joined: an id with a comma in it would
      // otherwise become two ids, and the Worker reads both shapes.
      for (const one of projects) params.append('project', one);
      for (const one of actors) params.append('actor', one);
      for (const one of actions) params.append('action', one);
      if (since) params.set('since', since);
      if (until) params.set('until', until);
      if (after) params.set('cursor', after);
      return params.toString();
    },
    [projects, actors, actions, since, until]
  );

  const load = useCallback(
    async (after: string | null): Promise<void> => {
      if (!chosen) return;
      try {
        const answer = await api.get<AuditAnswer>(apiPath`/orgs/${chosen.id}/audit?${apiQuery(new URLSearchParams(query(after)))}`);
        setError(null);
        setEntries((current) => (after && current ? [...current, ...answer.entries] : answer.entries));
        setCursor(answer.cursor);
        setTotal(answer.total);
      } catch (failure) {
        if (!after) setEntries([]);
        setError(failureText(failure, 'The audit could not be read.'));
      }
    },
    [chosen?.id, query]
  );

  useEffect(() => {
    setEntries(null);
    void load(null);
  }, [load]);

  useEffect(() => {
    if (!chosen) return;
    api
      .get<WorkspaceList>(apiPath`/orgs/${chosen.id}/workspaces`)
      .then((answer) => setWorkspaces(answer.workspaces))
      .catch(() => setWorkspaces([]));
    api
      .get<{ seats: Seat[] }>(apiPath`/orgs/${chosen.id}/seats`)
      .then((answer) => setSeats(answer.seats))
      .catch(() => setSeats([]));
  }, [chosen?.id]);

  if (!chosen) return <Empty title="No organization to audit">The audit is for the owner and the administrators of one.</Empty>;

  const narrowed = projects.length + actors.length + actions.length > 0 || Boolean(since) || Boolean(until);
  const clear = (): void => {
    setProjects([]);
    setActors([]);
    setActions([]);
    setSince('');
    setUntil('');
  };

  return (
    <>
      <Table
        columns={columns}
        header={['When', 'Who', 'Action', 'Project', 'Detail', '']}
        frame="clamp(640px, 75vh, 1440px)"
        toolbar={
          <>
            <OrgPicker orgs={orgs} value={chosen.id} onChange={onOrg} />
            <FilterBar
              end={
                <>
                  {entries !== null && <FilterCount shown={entries.length} total={total} narrowed={narrowed} noun={['line', 'lines']} />}
                  {/* The same frame as Clear beside it, the discreet one (§42.1):
                      it was once the one control on this row drawn in a
                      different shape, for no reason anybody could name. */}
                  <QuietButton
                    href={`${API_BASE}/orgs/${encodeURIComponent(chosen.id)}/audit.csv?${apiQuery(new URLSearchParams(query(null)))}`}
                    onClick={() => notify(`Export started — audit-${new Date().toISOString().slice(0, 10)}.csv`)}
                  >
                    Export CSV
                  </QuietButton>
                </>
              }
            >
              <MultiSelect
                label="Projects"
                all="Every project"
                value={projects}
                onChange={setProjects}
                options={workspaces.map((candidate) => ({ value: candidate.id, label: candidate.name }))}
              />
              <MultiSelect
                label="People"
                all="Everybody"
                value={actors}
                onChange={setActors}
                options={seats
                  .filter((seat) => seat.userId)
                  .map((seat) => ({ value: seat.userId ?? '', label: seat.email }))}
              />
              <MultiSelect
                label="Actions"
                all="Every action"
                value={actions}
                onChange={setActions}
                options={AUDIT_ACTIONS.map((candidate) => ({ value: candidate, label: candidate }))}
              />
              {/* One period, said as two ends of the same thing — both days are
                  included, so a week read on Friday is Monday to Friday. */}
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Input type="date" aria-label="From" value={since} max={until || undefined} onChange={(event) => setSince(event.target.value)} style={filterDate(Boolean(since))} />
                <span style={{ fontSize: 12.5, color: color.inkFaint }}>to</span>
                <Input type="date" aria-label="To" value={until} min={since || undefined} onChange={(event) => setUntil(event.target.value)} style={filterDate(Boolean(until))} />
              </span>
              {narrowed && <QuietButton onClick={clear}>Clear</QuietButton>}
            </FilterBar>
          </>
        }
      >
        {entries === null || phase !== 'content' ? (
          phase === 'skeleton' && <SkeletonRows columns={columns} />
        ) : (
          entries.map((entry) => (
            <Row key={entry.id} columns={columns}>
              <DateCell at={entry.at} style={{ fontFamily: font.mono, fontSize: 12, color: color.inkSubtle }} />
              <div style={{ minWidth: 0 }}>
                <div>{actorLabel(entry.actor)}</div>
                <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                  {entry.device ? (entry.device.label ?? 'a machine') : 'browser'}
                  {entry.country ? ` · ${entry.country}` : ''}
                </div>
              </div>
              <span
                style={{
                  fontFamily: font.mono,
                  fontSize: 12,
                  color: entry.action === 'secret.read' || entry.action === 'token.mismatch' || entry.action === 'machine.block'
                      ? color.warnText
                      : color.inkBody
                }}
              >
                {entry.action}
                {/* Whom it was done to, where it was done to a person — a role,
                    a hand-over, a machine cut off. */}
                {entry.subject && (
                  <span style={{ display: 'block', fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                    {actorLabel(entry.subject)}
                  </span>
                )}
              </span>
              <span style={{ minWidth: 0, color: entry.projectName ? color.inkBody : color.inkFaint }}>{entry.projectName ?? '—'}</span>
              <Detail entry={entry} />
              {entry.projectId && entry.version !== null && VERSIONED.has(entry.action) ? (
                <RowAction onClick={() => onOpenRevision(chosen.id, entry.projectId ?? '', entry.version ?? 0)}>
                  Open v{entry.version}
                </RowAction>
              ) : (
                <span />
              )}
            </Row>
          ))
        )}
      </Table>
      {entries !== null && entries.length === 0 && !error && (
        <div style={{ marginTop: 20 }}>
          {narrowed ? (
            <Empty title="No lines match these filters">
              Nothing in the audit fits every filter at once.
              <span style={{ display: 'block', marginTop: 12 }}>
                <HeaderButton onClick={clear}>Clear filters</HeaderButton>
              </span>
            </Empty>
          ) : (
            <Empty title="Nothing in the audit yet">
              Each publish, secret change, key approval and sign-in setting made in {chosen.name} is written here, with who did it and from where.
            </Empty>
          )}
        </div>
      )}
      {cursor && (
        <div style={{ marginTop: 16 }}>
          <HeaderButton
            disabled={older}
            onClick={() => {
              setOlder(true);
              void load(cursor).finally(() => setOlder(false));
            }}
          >
            {older ? 'Loading' : 'Load older lines'}
          </HeaderButton>
        </div>
      )}
      {error && entries !== null && entries.length === 0 ? (
        <PageError onRetry={() => load(null)} />
      ) : (
        error && <p style={{ fontSize: 13.5, color: color.badText, margin: '16px 0 0' }}>{error}</p>
      )}
    </>
  );
}

function Detail({ entry }: { entry: AuditEntry }): React.JSX.Element {
  const paths = entry.detail.paths ?? [];
  const parts = [
    entry.version !== null ? `v${entry.version}` : null,
    entry.detail.from !== undefined ? `from v${entry.detail.from}` : null,
    entry.detail.count !== undefined ? `${entry.detail.count} item${entry.detail.count === 1 ? '' : 's'}` : null
  ].filter(Boolean);
  return (
    <div style={{ minWidth: 0 }}>
      {parts.length > 0 && <div style={{ fontFamily: font.mono, fontSize: 12, color: color.inkSubtle }}>{parts.join(' · ')}</div>}
      {/* A long cell of a read table (§42.9): two lines at most, the whole
          list in the tooltip, reachable from the keyboard. */}
      {paths.length > 0 && (
        <WithTooltip
          text={paths.join(', ')}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontFamily: font.mono,
            fontSize: 11.5,
            color: color.inkFaint,
            marginTop: 3,
            wordBreak: 'break-all'
          }}
        >
          {paths.join(', ')}
        </WithTooltip>
      )}
      {parts.length === 0 && paths.length === 0 && <span style={{ color: color.inkFaint }}>—</span>}
    </div>
  );
}
